import type { JSX, ReactNode } from 'react';

import { useMediaQuery } from '../lib/use-media-query';
import { Card } from './card';
import { Table, TableBody, TableCell, TableHeader, TableRow } from './table';

export interface ResponsiveListColumn<T> {
  key: string;
  header: string;
  render: (item: T) => ReactNode;
  /** Hides the column in card layout (secondary data, table only). */
  cardHidden?: boolean;
}

export interface ResponsiveListProps<T> {
  items: T[];
  rowKey: (item: T) => string;
  columns: ResponsiveListColumn<T>[];
  /** Card layout (<640px): header with the primary datum. */
  renderCardHeader: (item: T) => ReactNode;
  /** Card layout: secondary data body. */
  renderCardBody: (item: T) => ReactNode;
  /** Per-row/card actions. */
  renderActions?: (item: T) => ReactNode;
  emptyState: ReactNode;
  caption: string;
  getRowTestId?: (item: T) => string;
}

/**
 * Renders `items` as a `Table` at `≥640px` and as a stack of `Card`s below
 * that, switching on `useMediaQuery('(min-width: 640px)')` — the same
 * breakpoint as Tailwind's `sm:`, deliberately distinct from web-admin's
 * 768px `useIsDesktop` (sidebar drawer).
 */
export function ResponsiveList<T>({
  items,
  rowKey,
  columns,
  renderCardHeader,
  renderCardBody,
  renderActions,
  emptyState,
  caption,
  getRowTestId,
}: ResponsiveListProps<T>): JSX.Element {
  const isDesktop = useMediaQuery('(min-width: 640px)');

  if (items.length === 0) {
    return <>{emptyState}</>;
  }

  if (isDesktop) {
    return (
      <Table>
        <caption className="sr-only">{caption}</caption>
        <thead>
          <TableRow>
            {columns.map((column) => (
              <TableHeader key={column.key}>{column.header}</TableHeader>
            ))}
            {renderActions === undefined ? null : <TableHeader>Acciones</TableHeader>}
          </TableRow>
        </thead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={rowKey(item)}>
              {columns.map((column) => (
                <TableCell key={column.key}>{column.render(item)}</TableCell>
              ))}
              {renderActions === undefined ? null : <TableCell>{renderActions(item)}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((item) => (
        <Card key={rowKey(item)} data-testid={getRowTestId?.(item)}>
          <div className="flex flex-col gap-3">
            <div className="font-medium text-slate-900">{renderCardHeader(item)}</div>
            <div className="grid grid-cols-2 gap-2 text-sm text-slate-700">
              {renderCardBody(item)}
            </div>
            {renderActions === undefined ? null : <div>{renderActions(item)}</div>}
          </div>
        </Card>
      ))}
    </div>
  );
}
